import { useState, useEffect } from 'react'
import { TrendingUp, Users, CheckSquare, AlertTriangle, IndianRupee, Megaphone, GitBranch } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { Permission } from '../../types/auth.types'
import { getEmployeesForFounder } from '../../services/auth.service'
import { getClients } from '../../services/clients.service'
import { getInvoices } from '../../services/invoices.service'
import { getDeals } from '../../services/deals.service'
import { getCampaigns } from '../../services/campaigns.service'
import { getProjects } from '../../services/projects.service'
import { getExpenses } from '../../services/expenses.service'
import type { Client, Invoice, Deal, Campaign, Project, Expense } from '../../types'
import type { User } from '../../types/auth.types'
import type { FounderSection } from './FounderLayout'

const GREETINGS: Record<string, string> = {
  bunny: 'What are we creating today, Bunny?',
  hj:    'Let\'s close some deals, HJ.',
  darshu:'Let\'s scale those campaigns, Darshu.',
}

interface Props { onNav: (s: FounderSection) => void }

export default function FounderHome({ onNav }: Props) {
  const { currentUser, hasPermission } = useAuth()
  const fKey = currentUser?.founderKey ?? 'hj'

  const greeting = GREETINGS[fKey] ?? `Welcome back, ${currentUser?.name}.`

  // Data state
  const [team,      setTeam]      = useState<User[]>([])
  const [clients,   setClients]   = useState<Client[]>([])
  const [invoices,  setInvoices]  = useState<Invoice[]>([])
  const [deals,     setDeals]     = useState<Deal[]>([])
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [projects,  setProjects]  = useState<Project[]>([])
  const [expenses,  setExpenses]  = useState<Expense[]>([])
  const [loading,   setLoading]   = useState(true)

  useEffect(() => {
    if (currentUser?.id) {
      getEmployeesForFounder(currentUser.id).then(setTeam).catch(console.error)
    }
  }, [currentUser?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetches: Promise<void>[] = []

    if (hasPermission(Permission.VIEW_CLIENTS)) {
      fetches.push(getClients().then(setClients).catch(console.error) as Promise<void>)
    }
    if (hasPermission(Permission.VIEW_INVOICES)) {
      fetches.push(getInvoices().then(setInvoices).catch(console.error) as Promise<void>)
    }
    if (hasPermission(Permission.VIEW_PIPELINE)) {
      fetches.push(getDeals().then(setDeals).catch(console.error) as Promise<void>)
    }
    if (hasPermission(Permission.VIEW_CAMPAIGNS) || hasPermission(Permission.VIEW_AD_PERFORMANCE)) {
      fetches.push(getCampaigns().then(setCampaigns).catch(console.error) as Promise<void>)
    }
    if (hasPermission(Permission.VIEW_PROJECTS)) {
      fetches.push(getProjects().then(setProjects).catch(console.error) as Promise<void>)
    }
    if (hasPermission(Permission.VIEW_EXPENSES)) {
      fetches.push(getExpenses().then(setExpenses).catch(console.error) as Promise<void>)
    }

    Promise.all(fetches).finally(() => setLoading(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Build relevant stats based on permissions
  const stats: { label: string; value: string; sub: string; color: string; icon: React.ElementType; section: FounderSection }[] = []

  if (hasPermission(Permission.VIEW_CLIENTS)) {
    const activeClients = clients.filter(c => c.status === 'active')
    const now = new Date()
    const newThisMonth = clients.filter(c => {
      const d = new Date(c.joinedAt)
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
    }).length
    stats.push({
      label: 'Active Clients',
      value: loading ? '—' : `${activeClients.length}`,
      sub: loading ? 'Loading…' : `+${newThisMonth} this month`,
      color: '#ffc600',
      icon: Users,
      section: 'clients',
    })
  }

  if (hasPermission(Permission.VIEW_INVOICES)) {
    const overdue = invoices.filter(i => i.status === 'overdue')
    const overdueTotal = overdue.reduce((s, i) => s + i.total, 0)
    stats.push({
      label: 'Overdue Invoices',
      value: loading ? '—' : `₹${(overdueTotal / 100000).toFixed(1)}L`,
      sub: loading ? 'Loading…' : `${overdue.length} invoice${overdue.length !== 1 ? 's' : ''} need action`,
      color: '#cc3300',
      icon: AlertTriangle,
      section: 'invoices',
    })
  }

  if (hasPermission(Permission.VIEW_PIPELINE)) {
    const activeDeals = deals.filter(d => d.stage !== 'closed-lost' && d.stage !== 'closed-won')
    const pipelineValue = activeDeals.reduce((s, d) => s + d.value, 0)
    const negotiating   = activeDeals.filter(d => d.stage === 'negotiation').length
    stats.push({
      label: 'Pipeline Value',
      value: loading ? '—' : `₹${(pipelineValue / 100000).toFixed(1)}L`,
      sub: loading ? 'Loading…' : `${negotiating} deal${negotiating !== 1 ? 's' : ''} in negotiation`,
      color: '#e8a000',
      icon: GitBranch,
      section: 'pipeline',
    })
  }

  if (hasPermission(Permission.VIEW_CAMPAIGNS)) {
    const activeCampaigns = campaigns.filter(c => c.status === 'active').length
    const totalSpend      = campaigns.reduce((s, c) => s + c.spend, 0)
    stats.push({
      label: 'Active Campaigns',
      value: loading ? '—' : `${activeCampaigns}`,
      sub: loading ? 'Loading…' : `₹${(totalSpend / 100000).toFixed(1)}L total spend`,
      color: '#ff8c00',
      icon: Megaphone,
      section: 'campaigns',
    })
  }

  if (hasPermission(Permission.VIEW_PROJECTS)) {
    const activeCount = projects.filter(p => p.status !== 'done').length
    const doneCount   = projects.filter(p => p.status === 'done').length
    stats.push({
      label: 'Active Projects',
      value: loading ? '—' : `${activeCount}`,
      sub: loading ? 'Loading…' : `${doneCount} completed`,
      color: '#ffc600',
      icon: CheckSquare,
      section: 'projects',
    })
  }

  if (hasPermission(Permission.VIEW_AD_PERFORMANCE)) {
    const withRoas = campaigns.filter(c => c.roas > 0)
    const avgRoas  = withRoas.length > 0
      ? withRoas.reduce((s, c) => s + c.roas, 0) / withRoas.length
      : 0
    stats.push({
      label: 'Avg ROAS',
      value: loading ? '—' : `${avgRoas.toFixed(2)}x`,
      sub: 'Across all active campaigns',
      color: '#ff8c00',
      icon: TrendingUp,
      section: 'ad-performance',
    })
  }

  if (hasPermission(Permission.VIEW_EXPENSES)) {
    const now = new Date()
    const thisMonthExpenses = expenses
      .filter(e => {
        const d = new Date(e.date)
        return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
      })
      .reduce((s, e) => s + e.amount, 0)
    stats.push({
      label: 'Expenses This Month',
      value: loading ? '—' : `₹${(thisMonthExpenses / 100000).toFixed(1)}L`,
      sub: 'This calendar month',
      color: '#e8a000',
      icon: IndianRupee,
      section: 'expenses',
    })
  }

  const topClients = [...clients]
    .filter(c => c.status === 'active')
    .sort((a, b) => b.mrr - a.mrr)
    .slice(0, 4)

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
          Good morning, {currentUser?.name} 👋
        </h1>
        <p className="text-sm mt-0.5" style={{ color: '#a0a0a0' }}>{greeting}</p>
      </div>

      {/* Stat cards */}
      {stats.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {stats.slice(0, 6).map(s => (
            <button key={s.label} onClick={() => onNav(s.section)} className="text-left transition-all">
              <div className="card-inner rounded-xl p-4 transition-all"
                style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(255,198,0,0.3)')}
                onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,198,0,0.12)')}>
                <div className="flex items-center gap-2 mb-2">
                  <s.icon size={15} style={{ color: s.color }} />
                  <p className="text-xs" style={{ color: '#a0a0a0' }}>{s.label}</p>
                </div>
                <p className="text-xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>{s.value}</p>
                <p className="text-xs mt-0.5" style={{ color: '#555' }}>{s.sub}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* My Team quick view */}
      <div className="rounded-xl p-5" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-bold text-white">My Team</h2>
          <button onClick={() => onNav('team')} className="text-xs" style={{ color: '#ffc600' }}>
            View all →
          </button>
        </div>
        {team.length === 0 ? (
          <p className="text-sm" style={{ color: '#555' }}>No employees assigned to you yet.</p>
        ) : (
          <div className="flex flex-wrap gap-3">
            {team.map(emp => (
              <div key={emp.id} className="flex items-center gap-2.5 px-3 py-2 rounded-lg"
                style={{ background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.08)' }}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                  style={{ background: '#ffc600', color: '#000', fontSize: '10px' }}>
                  {emp.avatarInitials}
                </div>
                <div>
                  <p className="text-sm font-medium text-white">{emp.name}</p>
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#e8a000' }} />
                    <span className="text-xs" style={{ color: '#555' }}>Active</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Top clients (if permitted) */}
      {hasPermission(Permission.VIEW_CLIENTS) && (
        <div className="rounded-xl p-5" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-white">Top Clients by MRR</h2>
            <button onClick={() => onNav('clients')} className="text-xs" style={{ color: '#ffc600' }}>View all →</button>
          </div>
          {loading ? (
            <p className="text-xs" style={{ color: '#555' }}>Loading…</p>
          ) : topClients.length === 0 ? (
            <p className="text-xs" style={{ color: '#555' }}>No active clients yet.</p>
          ) : (
            <div className="space-y-3">
              {topClients.map(client => (
                <div key={client.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                      style={{ background: '#ffc600', color: '#000', fontSize: '10px' }}>
                      {client.name[0]}
                    </div>
                    <span className="text-sm text-white">{client.name}</span>
                  </div>
                  <span className="text-sm font-semibold" style={{ color: '#ffc600' }}>
                    ₹{(client.mrr / 1000).toFixed(0)}k
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
